import { useTranslation } from '@axo/shared/i18n';
import { getFormattedAmountUI } from '@axo/shared/util/formatters';
import { calculateRate, calculateYearlyPayment } from '@axo/shared/util/loan';
import { Text } from '@axo/ui-core/components/typography';
import styles from './disclaimer.module.scss';

type Rates = {
  MIN: number;
  LOW: number;
  ESTABLISH: number;
  HIGH: number;
};

type DisclaimerProps = {
  appliedAmount: number;
  loanDuration: number;
  rates: Rates;
};

export function Disclaimer(props: DisclaimerProps) {
  const { t } = useTranslation();

  const tArgs = calculateDisclaimer(props);

  return (
    <Text
      as="p"
      size="xs"
      className={styles.disclaimer}
      data-testid="loan-example-info"
    >
      {t('Disclaimer', tArgs)}
    </Text>
  );
}

export const calculateDisclaimer = ({
  appliedAmount,
  loanDuration,
  rates,
}: DisclaimerProps) => {
  const establishCost = (rates.ESTABLISH / 100) * appliedAmount;

  const lowYearlyPayment = calculateYearlyPayment({
    loanAmount: appliedAmount,
    loanDuration,
    interestRate: rates.LOW / 100,
    establishCost,
  });
  const highYearlyPayment = calculateYearlyPayment({
    loanAmount: appliedAmount,
    loanDuration,
    interestRate: rates.HIGH / 100,
    establishCost,
  });

  const lowApr =
    (calculateRate(loanDuration, -lowYearlyPayment, appliedAmount) ?? 0) * 100;
  let highApr =
    (calculateRate(loanDuration, -highYearlyPayment, appliedAmount) ?? 0) * 100;

  const lowTotalPayment = Math.round(lowYearlyPayment * loanDuration);
  let highTotalPayment = Math.round(highYearlyPayment * loanDuration);

  // APR cap
  if (highApr > 25) {
    highApr = 25;
  }

  // Total payment cap
  if (highTotalPayment > appliedAmount * 2) {
    highTotalPayment = appliedAmount * 2;
  }

  return {
    rates,
    appliedAmount: getFormattedAmountUI(appliedAmount),
    establishCost: getFormattedAmountUI(establishCost),
    lowTotalPayment: getFormattedAmountUI(lowTotalPayment),
    highTotalPayment: getFormattedAmountUI(highTotalPayment),
    lowApr: lowApr.toFixed(2),
    highApr: highApr.toFixed(2),
  };
};
